import type { Locale } from "@tovi/shared";
import { displayName } from "@tovi/shared";
import type { Named } from "@tovi/shared";

/**
 * How the till reads the menu: the name a person sees, and the search that
 * finds it.
 *
 * The two live in one module on purpose. A search that matched a string the
 * tile does not render is not a feature, it is a tile that highlights for no
 * visible reason — so both go through `displayName` and cannot drift apart.
 *
 * Client-side, because the menu is already here. `GET /store/items` does carry
 * a `q`, but it matches the *base* name only and never a translation, and a
 * round trip per keystroke is exactly what a till on a restaurant's wifi
 * cannot afford. The whole catalogue is ≤200 rows in memory; filtering it is
 * microseconds and needs no debounce.
 *
 * `displayName` and `Named` live in `@tovi/shared` now — the back office and
 * the diner app need the same rule — and are re-exported here so every
 * caller in this app keeps a zero-line diff.
 */

export { displayName };
export type { Named };

/** An item, which additionally carries a code a cashier can type. */
export type Searchable = Named & { number: string | null };

/**
 * The line under the name: the code a cashier can type, and where the item
 * lives.
 *
 * Here rather than in the tile for the reason at the top of this module. The
 * code is searchable, so it has to be legible on the tile that matched; if the
 * two ever have to change together, this is the file that shows both.
 *
 * Truthiness rather than a null check: `ItemSchema.number` is nullable and its
 * max is 60, so `""` is a value the contract permits even though the back
 * office writes `number || null`. An empty code has to read as no code.
 *
 * `undefined` rather than `""` when there is neither, so the tile renders no
 * element at all instead of an empty muted line that still costs it a row.
 */
export function subline(item: Searchable, category: string | undefined): string | undefined {
  const parts = [item.number, category].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(" · ") : undefined;
}

/**
 * `toLowerCase` is a no-op for Khmer — the script is unicameral — and is here
 * for the Latin half of a bilingual menu, where it is the whole point.
 *
 * NFC is a guard rather than a fix: it collapses a decomposed `Café` onto the
 * composed one, which a menu with Italian names on it really does contain.
 *
 * Deliberately absent, and the reason this is a named function rather than an
 * inline `.toLowerCase()`: accent folding via `NFD` + strip `\p{M}`. It is the
 * obvious next "improvement" and it would strip the vowel signs and COENG
 * subscripts off every Khmer word, reducing them to consonant skeletons that
 * match each other. Do not add it.
 */
function normalize(value: string): string {
  return value.normalize("NFC").toLowerCase();
}

/**
 * The query as terms. Empty means "not searching", which is what a lone space
 * has to be — otherwise resting a thumb on the space bar blanks the category
 * rail.
 */
export function queryTerms(raw: string): string[] {
  const trimmed = normalize(raw).trim();
  return trimmed.length === 0 ? [] : trimmed.split(/\s+/);
}

/**
 * Collators are not free to build, and the query re-runs on every keystroke.
 * There are three locales, so this Map never grows past three entries.
 */
const collators = new Map<Locale, Intl.Collator>();

function collatorFor(locale: Locale): Intl.Collator {
  const found = collators.get(locale);
  if (found) return found;
  // `Intl` rather than `<`, which compares UTF-16 code units: that puts `Pâté`
  // after `Zabaglione` and orders Khmer by codepoint rather than by the
  // alphabet. `numeric` so "Pizza 2" precedes "Pizza 10".
  const made = new Intl.Collator(locale, { numeric: true });
  collators.set(locale, made);
  return made;
}

/**
 * A to Z by the name on the tile.
 *
 * Sorted by `displayName` for the same reason it is searched by it — a Khmer
 * till ordered by hidden English names is a list in no order at all.
 *
 * Returns a new array: the one handed in belongs to the query cache, and
 * `Array.prototype.sort` mutates in place.
 */
export function sortByName<T extends Named>(items: readonly T[], locale: Locale): T[] {
  const collator = collatorFor(locale);
  return [...items].sort((a, b) =>
    collator.compare(displayName(a, locale), displayName(b, locale)),
  );
}

/**
 * Every term must appear in the displayed name or the code, in any order.
 *
 * Order-independence is the only reason to split at all: a menu row reads
 * "Latte, Iced" and a cashier types "iced latte", which a plain substring
 * misses. For Khmer the split costs nothing — a Khmer query has no spaces, so
 * it yields a single term and the rule collapses back to plain substring,
 * which is the correct primitive for a script with no inter-word breaks.
 *
 * Substring rather than prefix for that same reason: a prefix matcher would
 * work in English and be useless in Khmer.
 */
export function matches(item: Searchable, terms: string[], locale: Locale): boolean {
  if (terms.length === 0) return true;
  const name = normalize(displayName(item, locale));
  // Joined so "latte PA03" can match across both. A term can never straddle
  // the join: terms are whitespace-free by construction.
  const haystack = item.number === null ? name : `${name} ${normalize(item.number)}`;
  return terms.every((term) => haystack.includes(term));
}
